import type { ExpenseEntry } from '@aeris/contracts'

import {
  dateValue,
  numberValue,
  textValue,
  type ExportColumn,
} from './export/table-export.ts'

/**
 * O caderno de gastos numa planilha — F187.
 *
 * *"no final ele sabe o que gastou"* (F146) sempre valeu para a tela; para o
 * contador, a única saída era print. Aqui saem as colunas que a tela mostra
 * **mais as que ela esconde por falta de espaço**: quem lançou, quem desfez e
 * por quê, o funcionário, e se aquilo descontou do salário.
 *
 * O desfeito **vai no arquivo**, com a situação escrita, pela mesma razão de
 * aparecer riscado na tela: um mês sem os estornos é um mês que não bate com o
 * caixa. Quem soma o arquivo filtra pela coluna "Situação" — e quem não
 * filtrar ao menos vê que existe.
 *
 * Dinheiro sai como número exato (`numberValue`), nunca por `Number`: é a
 * mesma regra do ADR-0016 que vale na tela.
 */

const SOURCE: Record<ExpenseEntry['paymentSource'], string> = {
  cash: 'Caixa',
  other: 'Pix/banco/outro',
}

const STATUS: Record<ExpenseEntry['status'], string> = {
  active: 'Vale',
  voided: 'Desfeito',
}

export const EXPENSE_EXPORT: readonly ExportColumn<ExpenseEntry>[] = [
  { id: 'dia', label: 'Dia', value: (row) => dateValue(row.spentOn) },
  { id: 'descricao', label: 'O que foi', value: (row) => row.description },
  {
    id: 'tipo',
    label: 'Tipo',
    value: (row) => (row.kind === 'salary' ? 'Folha' : 'Gasto'),
  },
  {
    id: 'categoria',
    label: 'Categoria',
    // O pagamento de salário não tem categoria de propósito (F146): ele é
    // folha, e contá-lo numa categoria somaria o mesmo dinheiro duas vezes.
    value: (row) =>
      row.kind === 'salary' ? 'Folha' : textValue(row.categoryName),
  },
  {
    id: 'funcionario',
    label: 'Funcionário',
    value: (row) => textValue(row.employeeName),
  },
  {
    id: 'desconta_salario',
    label: 'Desconta do salário',
    value: (row) => (row.deductsSalary ? 'Sim' : 'Não'),
  },
  {
    id: 'pago_com',
    label: 'Pago com',
    value: (row) => SOURCE[row.paymentSource],
  },
  { id: 'valor', label: 'Valor', value: (row) => numberValue(row.amount) },
  { id: 'situacao', label: 'Situação', value: (row) => STATUS[row.status] },
  {
    id: 'motivo_do_desfazer',
    label: 'Motivo do desfazer',
    value: (row) => textValue(row.voidReason),
  },
  {
    id: 'desfeito_por',
    label: 'Desfeito por',
    value: (row) => textValue(row.voidedByName),
  },
  {
    id: 'lancado_por',
    label: 'Lançado por',
    value: (row) => textValue(row.actorName),
  },
]
